import React, { createContext, useContext, useMemo, useState } from 'react';

import _get from 'lodash/get';

import { HTTP_STATUS } from 'constants/env';
import { AppContext } from 'store/AppContext';

import {
    createStatusError,
    getHttpStatus,
    isInvalidPartnerShortId,
} from './apiError';
import { fetchJitAccessTokenAPINew, getPartnerAPI } from './auth';
import { getCallAPI } from './call.js';
import { TOKEN_RETRY_PHASE, fetchAccessTokenWithRetry } from './tokenRetry';

const ApiContext = createContext();

export const useApi = () => {
    return useContext(ApiContext);
};

// eslint-disable-next-line react/prop-types
function ApiProvider({ children }) {
    const { setInitialAccessToken } = useContext(AppContext);
    const [tokenRetryPhase, setTokenRetryPhase] = useState(
        TOKEN_RETRY_PHASE.IDLE
    );

    const fetchData = async params => {
        try {
            const {
                partnerId,
                requestId,
                access_token: existingAccessToken,
            } = params;
            let access_token = existingAccessToken;

            if (!access_token) {
                const {
                    data: { access_token: newAccessToken },
                } = await fetchAccessTokenWithRetry(setTokenRetryPhase);
                access_token = newAccessToken;
            }
            setInitialAccessToken(access_token);
            const {
                data: { data },
            } = await getPartnerAPI({
                partnerId,
                requestId,
                access_token,
            });
            return {
                // accessToken,
                phoneNumber: data?.call_detail?.callback_number || '',
                access_token,
                ...data,
            };
        } catch (error) {
            const status = getHttpStatus(error);
            if (status === HTTP_STATUS.UNPROCESSABLE_ENTITY) {
                throw createStatusError(status, {
                    isInvalidPartnerShortId: isInvalidPartnerShortId(error),
                });
            }
            if (
                status === HTTP_STATUS.INTERNAL_SERVER_ERROR ||
                status === HTTP_STATUS.TOO_MANY_REQUESTS
            ) {
                throw createStatusError(status);
            }
            const errorMsg =
                (error?.response?.data?.data?.partner_short_id?.join('\n') ||
                    '') +
                (error?.response?.data?.data?.request_id?.join('\n') || '');
            throw errorMsg || error.message;
        }
    };

    const fetchCallDetails = async (partnerId, callId) => {
        try {
            const response = await fetchJitAccessTokenAPINew({ partnerId });
            const accessToken = _get(response, 'data.token', '');
            // const partner = await getPartnerAPI({ accessToken, partnerId });
            const call = await getCallAPI({ accessToken, partnerId, callId });
            return _get(call, 'data', {});
        } catch (error) {
            // eslint-disable-next-line no-console
            console.error('Error fetching data:', error);
            throw error;
        }
    };
    const apiValue = useMemo(
        () => ({
            fetchData,
            fetchCallDetails,
            tokenRetryPhase,
        }),
        [tokenRetryPhase]
    );

    return (
        <ApiContext.Provider value={apiValue}>{children}</ApiContext.Provider>
    );
}

export default ApiProvider;
